/* eslint-disable no-restricted-globals, no-restricted-syntax -- Native tools use the modern PWA browser baseline. */
/** Native battle analysis. All battle state stays in the browser. */
import type * as Calc from '../../node_modules/@smogon/calc/dist';
import { type Battle, Pokemon, type Side, type ServerPokemon } from './battle';
import { Dex, toID } from './battle-dex';
import { BattleToolsData, type BattleToolRole, type BattleToolSet } from './battle-tools-data';

declare const BattleCalc: typeof Calc;

export interface BattleToolAssumptions {
	role?: string;
	ability?: string;
	item?: string;
	tera?: string;
	stats?: Partial<Calc.StatsTable>;
}
export interface BattleToolCombatant {
	pokemon: Pokemon;
	server?: ServerPokemon;
	set?: BattleToolSet;
	role?: BattleToolRole;
	roleName: string;
	compatibleRoles: string[];
	assumptions: BattleToolAssumptions;
	moves: string[];
	revealed: string[];
	ability: string;
	item: string;
	abilityKnown: boolean;
	itemKnown: boolean;
	random: boolean;
}
export interface BattleToolDamage {
	move: string;
	type: string;
	min?: number;
	max?: number;
	text: string;
	detail: string;
}
export class BattleTools {
	static pending: Promise<void> | null = null;
	static engineVersion = '0.12.0';
	static engineStatus = '';
	static async checkEngineVersion() {
		try {
			const release = await BattleToolsData.fetchJSON('https://registry.npmjs.org/@smogon/calc/latest') as {
				version?: string,
			};
			this.engineStatus = typeof release.version === 'string' && /^\d+\.\d+\.\d+$/.test(release.version) &&
				release.version !== this.engineVersion ? `Damage engine update available: ${release.version}` : '';
		} catch {
			this.engineStatus = 'Engine update check unavailable';
		}
	}
	static get ready() { return typeof BattleCalc !== 'undefined'; }
	static loadEngine(): Promise<void> {
		if (this.ready) return Promise.resolve();
		if (this.pending) return this.pending;
		this.pending = new Promise<void>((resolve, reject) => {
			const script = document.createElement('script');
			const fail = () => {
				script.remove();
				this.pending = null;
				reject(new Error('Calculator download failed'));
			};
			const timer = setTimeout(fail, 15000);
			script.src = `/js/battle-calc.js?v=${this.engineVersion}`;
			script.onload = () => {
				clearTimeout(timer);
				if (this.ready) resolve();
				else fail();
			};
			script.onerror = () => { clearTimeout(timer); fail(); };
			document.head.appendChild(script);
		});
		return this.pending;
	}
	static isRandom(battle: Battle) {
		return battle.gen === 9 && toID(battle.tier) === 'gen9randombattle';
	}
	static team(battle: Battle, side: Side): Pokemon[] {
		if (!battle.atQueueEnd || !battle.myPokemon?.[0]?.ident.startsWith(`${side.sideid}:`)) return side.pokemon;
		return battle.myPokemon.map(server => {
			const seen = side.pokemon.find(pokemon => pokemon.ident === server.ident &&
				pokemon.speciesForme === server.speciesForme);
			if (seen) return seen;
			// A detached view of our private bench, without adding sprites or changing battle state.
			return Object.assign(Object.create(Pokemon.prototype) as Pokemon, server, {
				side, boosts: {}, volatiles: {}, turnstatuses: {}, movestatuses: {}, moveTrack: [],
				moddedType: [], statusData: { sleepTurns: 0, toxicTurns: 0 }, timesAttacked: 0,
				ability: server.ability || server.baseAbility, prevItem: '', itemEffect: '',
			});
		});
	}
	static serverPokemon(battle: Battle, pokemon: Pokemon): ServerPokemon | undefined {
		// Private requests describe the latest turn, never an earlier replay position.
		if (!battle.atQueueEnd) return undefined;
		return battle.myPokemon?.find(member => member.ident === pokemon.ident &&
			toID(member.speciesForme) === toID(pokemon.speciesForme));
	}
	static combatant(battle: Battle, pokemon: Pokemon, assumptions: BattleToolAssumptions = {}): BattleToolCombatant {
		const server = this.serverPokemon(battle, pokemon);
		const random = this.isRandom(battle);
		const set = random ? BattleToolsData.sets[toID(pokemon.speciesForme)] : undefined;
		const revealed = pokemon.moveTrack.filter(([move]) => !move.startsWith('*'))
			.map(([move]) => battle.dex.moves.get(move).name).filter(Boolean);
		const compatibleRoles = Object.keys(set?.roles || {}).filter(name =>
			revealed.every(move => set!.roles[name].moves.some(candidate => toID(candidate) === toID(move))));
		const roleName = assumptions.role && set?.roles[assumptions.role] ? assumptions.role :
			compatibleRoles[0] || Object.keys(set?.roles || {})[0] || '';
		const role = set?.roles[roleName];
		// A side that has already spent Tera cannot preview it on a different Pokémon.
		if (pokemon.side.terastallization && !pokemon.terastallized && assumptions.tera) {
			assumptions = { ...assumptions, tera: '' };
		}
		const abilityKnown = !!(server || pokemon.ability || pokemon.baseAbility);
		const itemKnown = !!(server || pokemon.item || pokemon.prevItem || pokemon.itemEffect);
		const ability = abilityKnown ? pokemon.effectiveAbility(server) :
			assumptions.ability ?? role?.abilities[0] ?? '';
		const itemName = server ? server.item :
			itemKnown ? pokemon.item : assumptions.item ?? role?.items[0] ?? '';
		const item = itemName ? battle.dex.items.get(itemName).name : '';
		const moves = server ? server.moves.map(move => battle.dex.moves.get(move).name) :
			Array.from(new Set(revealed.concat(role?.moves || [])));
		return { pokemon, server, set, role, roleName, compatibleRoles, assumptions, moves, revealed,
			ability, item, abilityKnown, itemKnown, random };
	}
	/** The engine clones its inputs. Preserve request/manual stats through that clone. */
	static withStats(pokemon: Calc.Pokemon, stats: Partial<Calc.StatsTable>): Calc.Pokemon {
		Object.assign(pokemon.rawStats, stats);
		Object.assign(pokemon.stats, stats);
		const clone = pokemon.clone.bind(pokemon);
		pokemon.clone = () => BattleTools.withStats(clone(), stats);
		return pokemon;
	}
	static createPokemon(battle: Battle, subject: BattleToolCombatant): Calc.Pokemon {
		const { pokemon, server, role, set, assumptions } = subject;
		const species = pokemon.getSpeciesForme(server);
		const gen = BattleCalc.Generations.get(9);
		if (!gen.species.get(toID(species))) throw new Error('Species is not supported by the damage engine');
		const evs: Partial<Calc.StatsTable> = {};
		const ivs: Partial<Calc.StatsTable> = {};
		for (const stat of Dex.statNames) {
			const ev = role?.evs?.[stat] ?? set?.evs?.[stat];
			const iv = role?.ivs?.[stat] ?? set?.ivs?.[stat];
			evs[stat] = ev !== undefined && ev >= 0 && ev <= 252 ? ev : subject.random ? 84 : 0;
			ivs[stat] = iv !== undefined && iv >= 0 && iv <= 31 ? iv : 31;
		}
		const tera = pokemon.terastallized || assumptions.tera;
		const abilityData = battle.dex.abilities.get(subject.ability);
		const ability = !abilityData.flags.cantsuppress && (pokemon.volatiles.gastroacid || battle.ngasActive()) ?
			'' : subject.ability;
		let item = subject.item;
		if (battle.hasPseudoWeather('Magic Room') || pokemon.volatiles.embargo || toID(ability) === 'klutz') item = '';
		const types = pokemon.getTypeList(server, true);
		const boostedStat = Dex.statNamesExceptHP.find(stat =>
			pokemon.volatiles[`protosynthesis${stat}`] || pokemon.volatiles[`quarkdrive${stat}`]);
		const result = new BattleCalc.Pokemon(9, species, {
			level: pokemon.level, ability: ability || '(No Ability)', item, evs, ivs, nature: 'Serious',
			boosts: pokemon.boosts, status: pokemon.status === '???' ? '' : pokemon.status,
			teraType: (tera || undefined) as Calc.State.Pokemon['teraType'],
			abilityOn: !!(boostedStat || pokemon.volatiles.flashfire || pokemon.volatiles.slowstart ||
				pokemon.volatiles.charge), boostedStat,
			alliesFainted: Math.min(5, pokemon.side.faintCounter),
			toxicCounter: pokemon.statusData.toxicTurns,
			gender: pokemon.gender,
			moves: subject.moves,
			overrides: { types: types.slice(0, 2) as Calc.Pokemon['types'] },
		});
		const stats = server ? { hp: server.maxhp, ...server.stats } : assumptions.stats || {};
		this.withStats(result, stats);
		const health = server || pokemon;
		result.originalCurHP = pokemon.fainted ? 0 : Math.max(1, Math.round(result.maxHP() * health.hp / health.maxhp));
		return result;
	}
	static field(battle: Battle, attacker: Pokemon, defender: Pokemon): Calc.Field {
		const weathers: { [id: string]: Calc.State.Field['weather'] } = {
			sunnyday: 'Sun', desolateland: 'Harsh Sunshine', raindance: 'Rain', primordialsea: 'Heavy Rain',
			sandstorm: 'Sand', hail: 'Hail', snow: 'Snow', deltastream: 'Strong Winds',
		};
		const terrain = (['Electric', 'Grassy', 'Psychic', 'Misty'] as const)
			.find(type => battle.hasPseudoWeather(`${type} Terrain`));
		const side = (pokemon: Pokemon): Calc.State.Side => ({
			isReflect: !!pokemon.side.sideConditions.reflect,
			isLightScreen: !!pokemon.side.sideConditions.lightscreen,
			isAuroraVeil: !!pokemon.side.sideConditions.auroraveil,
			isTailwind: !!pokemon.side.sideConditions.tailwind,
			isSeeded: !!pokemon.volatiles.leechseed,
			isSaltCured: !!pokemon.volatiles.saltcure,
			isCharge: !!pokemon.volatiles.charge,
			isPowerTrick: !!pokemon.volatiles.powertrick,
		});
		return new BattleCalc.Field({
			gameType: 'Singles', weather: weathers[battle.weather], terrain,
			isGravity: battle.hasPseudoWeather('Gravity'), isMagicRoom: battle.hasPseudoWeather('Magic Room'),
			isWonderRoom: battle.hasPseudoWeather('Wonder Room'),
			attackerSide: side(attacker), defenderSide: side(defender),
		});
	}
	static unsupported(battle: Battle, ...subjects: BattleToolCombatant[]): string {
		if (battle.gen !== 9 || battle.gameType !== 'singles') return 'Damage tools currently support Gen 9 singles.';
		if (battle.hardcoreMode) return 'Damage tools are hidden in hardcore mode.';
		for (const { pokemon, assumptions, item } of subjects) {
			if (item && !battle.dex.items.get(item).exists) return 'Enter a recognized item name, or leave it empty.';
			if (pokemon.fainted) return 'Choose a Pokémon that has not fainted.';
			if (pokemon.terastallized === 'Stellar' || assumptions.tera === 'Stellar') {
				return 'Stellar’s per-type boost history is not supported yet.';
			}
			if (pokemon.volatiles.transform || pokemon.volatiles.typeadd || pokemon.moddedType.length > 2) {
				return 'Transformed Pokémon and added types are not supported yet.';
			}
			if (pokemon.volatiles.powertrick || pokemon.volatiles.powerboost || pokemon.volatiles.guardboost ||
				pokemon.volatiles.powersplit || pokemon.volatiles.guardsplit) {
				return 'This stat-changing effect is not supported yet.';
			}
		}
		return '';
	}
	static calculate(battle: Battle, attacker: BattleToolCombatant, defender: BattleToolCombatant,
		moveName: string, crit = false): BattleToolDamage {
		const moveData = battle.dex.moves.get(moveName);
		const base = { move: moveData.name || moveName, type: moveData.type, detail: '' };
		if (!moveData.exists) return { ...base, text: 'Unknown move', detail: 'Enter a recognized move name.' };
		if (moveData.category === 'Status') return { ...base, text: 'Status', detail: 'No direct damage' };
		const unavailable = this.unsupported(battle, attacker, defender);
		if (unavailable) return { ...base, text: '—', detail: unavailable };
		// These depend on a choice/history not completely represented in the client model.
		if (['counter', 'mirrorcoat', 'metalburst', 'bide', 'beatup', 'pursuit', 'futuresight', 'doomdesire',
			'endeavor', 'finalgambit'].includes(moveData.id)) {
			return { ...base, text: 'Situational', detail: 'Depends on turn history or a future target' };
		}
		try {
			const source = this.createPokemon(battle, attacker);
			const target = this.createPokemon(battle, defender);
			const gen = BattleCalc.Generations.get(9);
			if (!gen.moves.get(moveData.id)) throw new Error('Unsupported move');
			const overrides: { basePower?: number } = {};
			if (moveData.id === 'ragefist') overrides.basePower = Math.min(350, 50 + 50 * attacker.pokemon.timesAttacked);
			if (moveData.id === 'lastrespects') overrides.basePower = 50 + 50 * attacker.pokemon.side.faintCounter;
			const move = new BattleCalc.Move(9, moveData.name, { ability: source.ability, item: source.item,
				isCrit: crit, overrides });
			const field = this.field(battle, attacker.pokemon, defender.pokemon);
			const result = BattleCalc.calculate(9, source, target, move, field);
			let [low, high] = result.range();
			let hitNote = move.hits > 1 ? `${move.hits} hits assumed` : '';
			if (Array.isArray(moveData.multihit) && !source.hasAbility('Skill Link')) {
				const minHits = source.hasItem('Loaded Dice') ? 4 : moveData.multihit[0];
				const maxHits = moveData.multihit[1];
				const options = { ability: source.ability, item: source.item, isCrit: crit, overrides };
				low = BattleCalc.calculate(9, source, target,
					new BattleCalc.Move(9, moveData.name, { ...options, hits: minHits }), field).range()[0];
				high = BattleCalc.calculate(9, source, target,
					new BattleCalc.Move(9, moveData.name, { ...options, hits: maxHits }), field).range()[1];
				hitNote = `${minHits}–${maxHits} hits`;
			}
			if (defender.pokemon.volatiles.glaiverush) { low *= 2; high *= 2; }
			if (!Number.isFinite(low) || !Number.isFinite(high)) throw new Error('Invalid damage range');
			const min = Math.floor(low / target.maxHP() * 1000) / 10;
			const max = Math.floor(high / target.maxHP() * 1000) / 10;
			const details = [`${low}–${high} HP`, 'if the move hits'];
			if (hitNote) details.push(hitNote);
			if (defender.pokemon.volatiles.substitute) details.push('target has a Substitute');
			return { ...base, type: result.move.type, min, max,
				text: `${min.toFixed(1)}–${max.toFixed(1)}%`, detail: details.join(' · ') };
		} catch {
			return { ...base, text: 'Unavailable', detail: 'The engine cannot calculate this matchup.' };
		}
	}
}
